import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useApp } from './context';
import { toast } from 'sonner';
import DashboardShell from './DashboardShell';
import {
  Users, BookOpen, Mic, Cpu, DollarSign, BarChart3, TrendingUp,
  TrendingDown, FileText, ClipboardList, Plus, Eye, Wifi
} from './components/DeskeyIcons';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Skeleton } from './components/ui/skeleton';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// KPI Card Component
const KPICard = ({ title, value, subtitle, icon: Icon, color, bg, trend, trendValue }) => (
  <div className="kpi-card">
    <div className="flex items-start justify-between">
      <div>
        <p className="kpi-label">{title}</p>
        <p className="kpi-number" style={{ color }}>{value ?? '—'}</p>
        {subtitle && <p className="text-xs mt-1 text-[hsl(var(--muted-foreground))]">{subtitle}</p>}
      </div>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: bg }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
    </div>
    {trend && (
      <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${trend === 'up' ? 'text-[#00AA8D]' : 'text-[#F05545]'}`}>
        {trend === 'up' ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
        {trendValue}
      </div>
    )}
  </div>
);

export default function AdminOverview() {
  const { user, token } = useApp();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [devices, setDevices] = useState([]);
  const [recentLessons, setRecentLessons] = useState([]);
  const [recentRecordings, setRecentRecordings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || !token) return;
    const tid = user?.tenant_id;
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/analytics/overview`, { params: { tenant_id: tid }, headers }),
      axios.get(`${API}/devices`, { params: { tenant_id: tid }, headers }),
      axios.get(`${API}/published-lessons`, { params: { tenant_id: tid }, headers }),
      axios.get(`${API}/recording-sessions`, { params: { tenant_id: tid }, headers })
    ]).then(([a, d, l, r]) => {
      setAnalytics(a.data);
      setDevices(d.data.slice(0, 4));
      setRecentLessons(l.data.slice(0, 5));
      setRecentRecordings(r.data.slice(0, 5));
    }).catch(err => {
      console.error('Failed to load admin overview:', err);
      toast.error('Failed to load overview');
    }).finally(() => setLoading(false));
  }, [user?.tenant_id]);

  if (loading) return (
    <DashboardShell title="Overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-5">
        {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}
      </div>
    </DashboardShell>
  );

  const a = analytics || {};
  const chartData = [
    { name: 'Lessons', value: a.lessons_published || 0, fill: '#00AA8D' },
    { name: 'Recordings', value: a.recordings_completed || 0, fill: '#3B82F6' },
    { name: 'Quizzes', value: a.quizzes_published || 0, fill: '#F97316' },
    { name: 'Attempts', value: a.quiz_attempts || 0, fill: '#8B5CF6' },
  ];

  return (
    <DashboardShell title="Admin Overview" breadcrumb="Dashboard">
      <div className="space-y-5 fade-in">
        {/* KPI Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard title="Teachers" value={a.active_teachers} icon={Users}
            color="#00AA8D" bg="hsl(167,100%,95%)" trend="up" trendValue="+2 this month" />
          <KPICard title="Students" value={a.active_students} icon={Users}
            color="#3B82F6" bg="hsl(217,91%,95%)" />
          <KPICard title="Billable" value={a.billable_students} icon={DollarSign}
            color="#F97316" bg="hsl(25,95%,95%)" subtitle={`₹${a.projected_monthly_billing?.toFixed(0) || 0}/mo`} />
          <KPICard title="Lessons" value={a.lessons_published} icon={BookOpen}
            color="#8B5CF6" bg="hsl(258,90%,95%)" trend="up" trendValue="+3 this week" />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard title="Recordings" value={a.recordings_completed} icon={Mic}
            color="#3B82F6" bg="hsl(217,91%,95%)" />
          <KPICard title="Pending Drafts" value={a.drafts_pending_review} icon={FileText}
            color="#F97316" bg="hsl(25,95%,95%)" />
          <KPICard title="Devices" value={`${a.devices_online || 0}/${a.devices_total || 0}`} icon={Cpu}
            color="#00AA8D" bg="hsl(167,100%,95%)" />
          <KPICard title="Quiz Attempts" value={a.quiz_attempts} icon={ClipboardList}
            color="#8B5CF6" bg="hsl(258,90%,95%)" />
        </div>

        {/* Charts & Devices Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Activity Bar Chart */}
          <Card className="rounded-xl border border-[hsl(var(--border))]">
            <CardHeader className="pb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[hsl(167,100%,95%)]">
                  <BarChart3 className="w-4 h-4 text-[#00AA8D]" />
                </div>
                <CardTitle className="text-base">Platform Activity</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={chartData} barSize={40}>
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: 'white', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Device Status */}
          <Card className="rounded-xl border border-[hsl(var(--border))]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[hsl(167,100%,95%)]">
                    <Cpu className="w-4 h-4 text-[#00AA8D]" />
                  </div>
                  <CardTitle className="text-base">Devices</CardTitle>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/admin/devices')} className="text-xs h-8">
                  Manage
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {devices.length === 0 ? (
                <div className="empty-state py-6">
                  <Cpu className="empty-state-icon" />
                  <p className="text-sm font-medium">No devices registered</p>
                  <Button size="sm" className="mt-3 bg-[#00AA8D] hover:bg-[#009980]" onClick={() => navigate('/admin/devices')}>
                    <Plus className="w-4 h-4 mr-1" /> Add Device
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {devices.map(d => (
                    <div key={d.id} className="flex items-center justify-between p-3 rounded-lg border border-[hsl(var(--border))] bg-white">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[hsl(var(--accent))]">
                          <Cpu className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{d.name}</p>
                          <p className="text-xs text-[hsl(var(--muted-foreground))]">S/N: {d.serial_number}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-medium">{d.battery_level}%</span>
                        <div className="flex items-center gap-1.5">
                          <span className={`pulse-dot ${d.connectivity_status}`}></span>
                          <span className={`text-xs capitalize ${d.connectivity_status === 'online' ? 'text-[#00AA8D]' : 'text-[hsl(var(--muted-foreground))]'}`}>
                            {d.connectivity_status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Recent Lessons */}
          <Card className="rounded-xl border border-[hsl(var(--border))]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[hsl(258,90%,95%)]">
                    <BookOpen className="w-4 h-4 text-[#8B5CF6]" />
                  </div>
                  <CardTitle className="text-base">Recent Lessons</CardTitle>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/admin/lessons')} className="text-xs h-8">
                  View all
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {recentLessons.length === 0 ? (
                <div className="empty-state py-6">
                  <BookOpen className="empty-state-icon" />
                  <p className="text-sm font-medium">No lessons yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentLessons.slice(0, 4).map(l => (
                    <div key={l.id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-[hsl(var(--accent))] transition-colors cursor-pointer"
                      onClick={() => navigate(`/admin/lessons`)}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{l.title}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">{l.views || 0} views</p>
                      </div>
                      <Badge className="text-xs status-published ml-2">Published</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Recordings */}
          <Card className="rounded-xl border border-[hsl(var(--border))]">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[hsl(217,91%,95%)]">
                    <Mic className="w-4 h-4 text-[#3B82F6]" />
                  </div>
                  <CardTitle className="text-base">Recent Recordings</CardTitle>
                </div>
                <Button variant="ghost" size="sm" className="text-xs h-8">View all</Button>
              </div>
            </CardHeader>
            <CardContent>
              {recentRecordings.length === 0 ? (
                <div className="empty-state py-6">
                  <Mic className="empty-state-icon" />
                  <p className="text-sm font-medium">No recordings yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentRecordings.slice(0, 4).map(r => (
                    <div key={r.id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-[hsl(var(--accent))] transition-colors">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{r.topic}</p>
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">
                          {r.duration ? `${Math.floor(r.duration / 60)}m` : 'In progress'}
                        </p>
                      </div>
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium status-${r.status}`}>
                        {r.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Billing Snapshot */}
        <Card className="rounded-xl border border-[hsl(var(--border))]">
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[hsl(25,95%,95%)]">
                <DollarSign className="w-4 h-4 text-[#F97316]" />
              </div>
              <CardTitle className="text-base">Billing Snapshot</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Active Students</p>
                <p className="text-xl font-bold mt-0.5" style={{ fontFamily: 'Space Grotesk' }}>{a.active_students}</p>
              </div>
              <div>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Billable Students</p>
                <p className="text-xl font-bold mt-0.5" style={{ fontFamily: 'Space Grotesk' }}>{a.billable_students}</p>
              </div>
              <div>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Projected Monthly</p>
                <p className="text-xl font-bold mt-0.5 text-[#F97316]" style={{ fontFamily: 'Space Grotesk' }}>₹{a.projected_monthly_billing?.toFixed(2)}</p>
              </div>
              <div>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Projected Yearly</p>
                <p className="text-xl font-bold mt-0.5" style={{ fontFamily: 'Space Grotesk' }}>₹{a.projected_yearly_billing?.toFixed(2)}</p>
              </div>
            </div>
            <div className="mt-4">
              <Button size="sm" variant="outline" onClick={() => navigate('/admin/billing')} className="text-xs">
                View Full Billing <DollarSign className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardShell>
  );
}
